/**
 * Session state.
 *
 * Owns the access token, the signed-in user, and the refresh schedule. Everything
 * that needs a token reads it from here.
 *
 * ## The token is a ref, not state
 *
 * `accessTokenRef` rather than `useState`. The token is read by the API client
 * and by the socket on every request and every reconnect, and none of those
 * reads should cause a re-render - nor should a re-render be required for them to
 * see a new value. Putting it in state would also mean that a refresh, which
 * happens on a timer, re-renders the whole tree for a value nothing renders.
 *
 * `user` *is* state, because the UI renders it.
 *
 * ## Proactive refresh
 *
 * A timer fires one minute before `expiresAt` and rotates the token. The
 * alternative - waiting for a 401 - means the user's next click pays a refresh
 * round trip on top of the request they actually made, and the first request
 * after every fifteen-minute idle period feels slow for no visible reason. The
 * 401 path in `api/client.ts` still exists as the backstop, because a laptop that
 * was asleep will have missed the timer entirely.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@slate/shared';
import { ApiClient, refreshSessionRequest } from '../api/client.js';

/** Refresh this far before expiry. Long enough to survive a slow network, short
 * enough that the token is still comfortably valid when the request lands. */
const REFRESH_LEAD_MS = 60_000;

/** Floor on the refresh delay, for a token that is already expired or that the
 * server issued with an implausibly short life. Without it the timer would fire
 * immediately and the client would hammer the refresh endpoint. */
const MIN_REFRESH_DELAY_MS = 5_000;

export interface AuthContextValue {
  user: User | null;
  /** True until the initial `refresh` probe resolves. The UI shows a splash
   * rather than the sign-in form, because a signed-in user reloading the page
   * should not see a login form flash. */
  status: 'checking' | 'signed-out' | 'signed-in';
  client: ApiClient;
  /** The current access token, or `null`. A function rather than a value: the
   * token rotates on a timer and a captured string would be the one from
   * whenever the consumer last rendered. `BoardProvider` pushes this into the
   * socket on every connect. */
  getAccessToken(): string | null;
  register(input: { email: string; name: string; password: string }): Promise<void>;
  login(input: { email: string; password: string }): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('checking');

  const accessTokenRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiresAtRef = useRef<number>(0);

  /** Applies a session response: stores the token, sets the user, schedules the
   * next rotation. The single place any of those three happens. */
  const applySession = useCallback((next: { user: User; accessToken: string; expiresAt: string }) => {
    accessTokenRef.current = next.accessToken;
    expiresAtRef.current = Date.parse(next.expiresAt);
    setUser(next.user);
    setStatus('signed-in');
  }, []);

  const clearSession = useCallback(() => {
    accessTokenRef.current = null;
    expiresAtRef.current = 0;
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setUser(null);
    setStatus('signed-out');
  }, []);

  /**
   * The client is constructed once and closes over stable callbacks, so its
   * identity never changes. That matters because `BoardProvider` puts it in an
   * effect dependency list, and a client that was recreated on every render
   * would tear down and reopen the socket on every render.
   */
  const client = useMemo(
    () =>
      new ApiClient({
        getAccessToken: () => accessTokenRef.current,
        refresh: async () => {
          // The same free function the initial probe uses. Constructing a second
          // `ApiClient` to reach it would be a circular dependency waiting to
          // happen: the second client would need its own refresh hook.
          const session = await refreshSessionRequest();
          if (session === null) return null;
          applySession(session);
          return session.accessToken;
        },
        onSessionLost: () => clearSession(),
      }),
    [applySession, clearSession],
  );

  // The refresh scheduler. Re-runs whenever the expiry changes, which is exactly
  // when a new rotation should be planned.
  useEffect(() => {
    if (status !== 'signed-in') return;
    if (refreshTimerRef.current !== null) clearTimeout(refreshTimerRef.current);

    const delay = Math.max(MIN_REFRESH_DELAY_MS, expiresAtRef.current - Date.now() - REFRESH_LEAD_MS);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      void client.refreshSession().then((session) => {
        if (session === null) {
          // The refresh cookie is gone or was rejected. Signing out here rather
          // than waiting for a 401 means the UI reaches the correct state
          // without a failed request first.
          clearSession();
          return;
        }
        applySession(session);
      });
    }, delay);

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [status, user?.id, client, applySession, clearSession]);

  // The initial probe. The refresh token is in an HttpOnly cookie, so the only
  // way to know whether the session is alive is to ask.
  useEffect(() => {
    let cancelled = false;
    void client.refreshSession().then((session) => {
      if (cancelled) return;
      if (session === null) {
        setStatus('signed-out');
        return;
      }
      applySession(session);
    });
    return () => {
      cancelled = true;
    };
  }, [client, applySession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      client,
      getAccessToken: () => accessTokenRef.current,
      async register(input) {
        applySession(await client.register(input));
      },
      async login(input) {
        applySession(await client.login(input));
      },
      async logout() {
        // Fire and forget. The local session is cleared regardless: a logout that
        // failed to reach the server still means the user is done, and leaving
        // them signed in because of a network error would be the wrong call.
        await client.logout().catch(() => undefined);
        clearSession();
      },
    }),
    [user, status, client, applySession, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}

/** The current user, for components that are only rendered when signed in.
 * Throws rather than returning `null` so a component cannot silently render a
 * blank avatar for a user that should exist. */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (user === null) throw new Error('useCurrentUser was called outside a signed-in tree.');
  return user;
}
